/**
 * Hook para gerenciamento de vendedores do CRM
 * 
 * Fornece operações CRUD para vendedores:
 * - useSellers(): lista vendedores do tenant ativo
 * - createSeller: mutation para criar novo vendedor
 * - updateSeller: mutation para atualizar vendedor existente
 * - deleteSeller: mutation para excluir vendedor
 * 
 * @version 1.0.0
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger para este módulo
const logger = createLogger('CrmSellers');
// Tipo do vendedor retornado pelo banco
export interface CrmSeller {
  id: string;
  tenant_id: string;
  user_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  commission_rate: number;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Campo virtual para join com profiles (usuário vinculado)
  user?: {
    id: string;
    full_name: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null;
}

// Input para criação de vendedor
export interface CreateSellerInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  user_id?: string | null;
  commission_rate?: number;
  is_active?: boolean;
}

// Input para atualização de vendedor
export interface UpdateSellerInput extends Partial<CreateSellerInput> {
  id: string;
}

/**
 * Hook principal para gerenciamento de vendedores
 */
export function useCrmSellers() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Query para listar vendedores
  const { data: sellers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['crm-sellers', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug('[useCrmSellers] Buscando vendedores do tenant:', tenantId);

      // Busca via RPC server-side (segurança: não expõe tenant_id/metadata)
      const { data, error } = await supabase.rpc(
        'get_tenant_sellers_safe',
        { p_active_only: false, p_tenant_hint: tenantId }
      );

      if (error) {
        logger.error('[useCrmSellers] Erro ao buscar vendedores:', error);
        throw error;
      }

      // Mapeia resposta RPC para interface CrmSeller (campo user virtual)
      const mappedData = (data || []).map((seller: any) => ({
        ...seller,
        user: seller.user_full_name ? {
          id: seller.user_id || '',
          full_name: seller.user_full_name,
          email: seller.email,
          avatar_url: seller.user_avatar_url,
        } : null,
      }));

      logger.debug('[useCrmSellers] Vendedores encontrados:', mappedData.length);
      return mappedData as CrmSeller[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });

  // Query para listar apenas vendedores ativos (para seletores)
  // Inclui JOIN com profiles para herdar avatar do usuário vinculado
  const { data: activeSellers = [] } = useQuery({
    queryKey: ['crm-sellers-active', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      // Busca via RPC server-side (apenas ativos, para seletores)
      const { data, error } = await supabase.rpc(
        'get_tenant_sellers_safe',
        { p_active_only: true, p_tenant_hint: tenantId }
      );

      if (error) {
        logger.error('[useCrmSellers] Erro ao buscar vendedores ativos:', error);
        throw error;
      }

      // Processa para usar avatar do usuário como fallback
      const processedData = (data || []).map((seller: any) => ({
        ...seller,
        avatar_url: seller.user_avatar_url || seller.avatar_url || null,
      }));

      return processedData as Pick<CrmSeller, 'id' | 'name' | 'avatar_url' | 'email' | 'phone' | 'user_id'>[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Mutation para criar vendedor
  const createSeller = useMutation({
    mutationFn: async (input: CreateSellerInput) => {
      if (!tenantId) throw new Error('Tenant não selecionado');

      logger.debug('[useCrmSellers] Criando vendedor:', input.name);

      const { data, error } = await (supabase as any)
        .from('crm_sellers')
        .insert({
          tenant_id: tenantId,
          name: input.name,
          email: input.email || null,
          phone: input.phone || null,
          avatar_url: input.avatar_url || null,
          user_id: input.user_id || null,
          commission_rate: input.commission_rate ?? 0,
          is_active: input.is_active ?? true,
        })
        .select()
        .single();

      if (error) {
        logger.error('[useCrmSellers] Erro ao criar vendedor:', error);
        throw error;
      }

      return data as CrmSeller;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-sellers', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['crm-sellers-active', tenantId] });
      toast.success('Vendedor criado com sucesso');
    },
    onError: (error) => {
      logger.error('[useCrmSellers] Falha ao criar vendedor:', error);
      toast.error('Erro ao criar vendedor');
    },
  });

  // Mutation para atualizar vendedor
  const updateSeller = useMutation({
    mutationFn: async (input: UpdateSellerInput) => {
      const { id, ...updates } = input;

      logger.debug('[useCrmSellers] Atualizando vendedor:', id);

      const { data, error } = await (supabase as any)
        .from('crm_sellers')
        .update({
          name: updates.name,
          email: updates.email,
          phone: updates.phone,
          avatar_url: updates.avatar_url,
          user_id: updates.user_id,
          commission_rate: updates.commission_rate,
          is_active: updates.is_active,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('[useCrmSellers] Erro ao atualizar vendedor:', error);
        throw error;
      }

      return data as CrmSeller;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-sellers', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['crm-sellers-active', tenantId] });
      toast.success('Vendedor atualizado com sucesso');
    },
    onError: (error) => {
      logger.error('[useCrmSellers] Falha ao atualizar vendedor:', error);
      toast.error('Erro ao atualizar vendedor');
    },
  });

  // Mutation para excluir vendedor
  const deleteSeller = useMutation({
    mutationFn: async (id: string) => {
      logger.debug('[useCrmSellers] Excluindo vendedor:', id);

      const { error } = await (supabase as any)
        .from('crm_sellers')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('[useCrmSellers] Erro ao excluir vendedor:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-sellers', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['crm-sellers-active', tenantId] });
      toast.success('Vendedor excluído com sucesso');
    },
    onError: (error) => {
      logger.error('[useCrmSellers] Falha ao excluir vendedor:', error);
      toast.error('Erro ao excluir vendedor');
    },
  });

  return {
    sellers,
    activeSellers,
    isLoading,
    error,
    refetch,
    createSeller,
    updateSeller,
    deleteSeller,
  };
}
